import React, { useCallback, useEffect, useMemo, useState } from "react";
import { arrayUnion, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  createAttendanceRecord,
  fetchAttendanceRecords,
  fetchAttendanceUsers,
  updateAttendanceRecord,
} from "../services/attendanceService";

const STAFF_FILTERS = { ACTIVE: "active", INACTIVE: "inactive", ALL: "all" };

const getStaffName = (staffMember) =>
  staffMember?.name || staffMember?.username || staffMember?.email || "Unnamed Staff";

const isStaffActive = (staffMember) => {
  if (staffMember?.status) return String(staffMember.status).toLowerCase() !== "inactive";
  return staffMember?.isActive !== false;
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("en-GB");
};

const formatTime = (value) => {
  if (!value) return "—";
  const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};



const isClientAttendance = (record = {}) => {
  const type = String(record.locationType || "").toLowerCase();
  const location = String(record.location || "").toLowerCase();
  return type === "client" || location === "client" || location === "client visit";
};

const getDisplayLocation = (record = {}) => {
  if (record.status === "Absent" || record.notEmployed) return record.location || "-";
  return isClientAttendance(record) ? "Client Visit" : (record.location || "Office");
};

const getDetailedLocation = (record = {}) => {
  if (record.status === "Absent" || record.notEmployed) return "—";
  if (isClientAttendance(record)) {
    return record.clientName ? `Client: ${record.clientName}` : "Client Visit";
  }
  return record.location || record.officeName || "Office";
};

const toDateTimeLocalValue = (value) => {
  if (!value) return "";
  const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60 * 1000).toISOString().slice(0, 16);
};

const emptyCorrectionForm = {
  status: "Absent",
  location: "Office",
  clientName: "",
  markedAt: "",
  reason: "",
};

const toLocalISO = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function AttendanceReport({ embedded = false }) {
  const { user, userData } = useAuth();
  const role = String(userData?.role || "staff").toLowerCase();
  const isStaffView = role === "staff";
  const isAdmin = role === "admin";
  const currentUserId = userData?.id || user?.uid || "";

  const [attendance, setAttendance] = useState([]);
  const [staff, setStaff] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [staffStatusFilter, setStaffStatusFilter] = useState(STAFF_FILTERS.ACTIVE);
  const [showAttendanceDetails, setShowAttendanceDetails] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [correctionTarget, setCorrectionTarget] = useState(null);
  const [correctionForm, setCorrectionForm] = useState(emptyCorrectionForm);
  const [savingCorrection, setSavingCorrection] = useState(false);
  const [correctionError, setCorrectionError] = useState("");

  useEffect(() => {
    const today = new Date();
    if (isStaffView) {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      setStartDate(toLocalISO(start));
      setEndDate(toLocalISO(today));
    } else {
      setStartDate(toLocalISO(new Date(today.getFullYear(), today.getMonth(), 1)));
      setEndDate(toLocalISO(new Date(today.getFullYear(), today.getMonth() + 1, 0)));
    }
  }, [isStaffView]);

  useEffect(() => {
    if (isStaffView) {
      setStaff([{ id: currentUserId, ...userData }]);
      return undefined;
    }

    let cancelled = false;
    fetchAttendanceUsers()
      .then((rows) => {
        if (cancelled) return;
        const allStaff = rows
          .filter((item) => String(item.role || "").toLowerCase() === "staff")
          .sort((a, b) => getStaffName(a).localeCompare(getStaffName(b)));
        setStaff(allStaff);
        setError("");
      })
      .catch((loadError) => {
        console.error("Unable to load staff:", loadError);
        if (!cancelled) setError("Unable to load staff records. Please refresh and try again.");
      });

    return () => { cancelled = true; };
  }, [isStaffView, currentUserId, userData]);

  const loadAttendance = useCallback(async () => {
    if (!startDate || !endDate || startDate > endDate || !currentUserId) {
      setAttendance([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Staff query is deliberately constrained by userId. This prevents access
      // to other users' records and avoids a broad attendance collection read.
      const fetchedRows = await fetchAttendanceRecords({
        isStaffView,
        userId: currentUserId,
        startDate,
        endDate,
      });
      const rows = fetchedRows.filter((item) => item.date >= startDate && item.date <= endDate);
      setAttendance(rows);
    } catch (loadError) {
      console.error("Unable to load attendance:", loadError);
      setError("Unable to load attendance records. Please refresh and try again.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, isStaffView, currentUserId]);

  useEffect(() => {
    loadAttendance();
  }, [loadAttendance]);

  const filteredStaff = useMemo(() => {
    if (isStaffView) return staff;
    if (staffStatusFilter === STAFF_FILTERS.ALL) return staff;
    return staff.filter((staffMember) => {
      const active = isStaffActive(staffMember);
      return staffStatusFilter === STAFF_FILTERS.ACTIVE ? active : !active;
    });
  }, [staff, staffStatusFilter, isStaffView]);

  const dateRange = useMemo(() => {
    if (!startDate || !endDate || startDate > endDate) return [];
    const dates = [];
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    for (let current = new Date(start); current <= end; current.setDate(current.getDate() + 1)) {
      dates.push(toLocalISO(current));
    }
    return dates;
  }, [startDate, endDate]);

  const wasEmployedOnDate = (staffMember, date) => {
    const joiningDate = staffMember?.joiningDate || "";
    const leavingDate = staffMember?.leavingDate || "";
    if (joiningDate && date < joiningDate) return false;
    if (leavingDate && date > leavingDate) return false;
    return true;
  };

  const getAttendanceForStaffOnDate = (staffMember, date) => {
    if (!wasEmployedOnDate(staffMember, date)) {
      return { status: "Not Employed", location: "-", clientName: "", markedAt: null, notEmployed: true };
    }
    const record = attendance.find((item) => item.userId === staffMember.id && item.date === date);
    if (!record) return { id: null, status: "Absent", location: "-", clientName: "", markedAt: null, notEmployed: false };
    return {
      ...record,
      id: record.id,
      status: record.status || "Absent",
      location: getDisplayLocation(record),
      clientName: record.clientName || "",
      markedAt: record.markedAt || record.createdAt || null,
      notEmployed: false,
    };
  };

  const statusCode = (status) => ({
    "Full Day": "P", "Half Day": "HD", Absent: "A", "Not Employed": "NE", Leave: "L",
  }[status] || status || "A");

  const totalsForStaff = (staffMember) => dateRange.reduce((totals, date) => {
    const status = getAttendanceForStaffOnDate(staffMember, date).status;
    if (status === "Full Day") totals.present += 1;
    else if (status === "Half Day") totals.halfDay += 1;
    else if (status === "Absent") totals.absent += 1;
    return totals;
  }, { present: 0, halfDay: 0, absent: 0 });

  const detailedRows = () => filteredStaff.flatMap((staffMember) =>
    dateRange.map((date) => {
      const cell = getAttendanceForStaffOnDate(staffMember, date);
      return {
        Staff: getStaffName(staffMember),
        Date: date,
        Status: cell.status,
        Location: getDetailedLocation(cell),
        Time: formatTime(cell.markedAt),
      };
    })
  );

  const exportToExcel = () => {
    const rows = showAttendanceDetails ? detailedRows() : filteredStaff.map((staffMember) => {
      const row = {
        Staff: getStaffName(staffMember),
        "Employment Status": isStaffActive(staffMember) ? "Active" : "Inactive",
        "Joining Date": staffMember.joiningDate || "",
        "Leaving Date": staffMember.leavingDate || "",
      };
      dateRange.forEach((date) => { row[date] = statusCode(getAttendanceForStaffOnDate(staffMember, date).status); });
      const totals = totalsForStaff(staffMember);
      row.Present = totals.present;
      row["Half Day"] = totals.halfDay;
      row.Absent = totals.absent;
      return row;
    });
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, showAttendanceDetails ? "Attendance Detail" : "Attendance");
    XLSX.writeFile(workbook, `Attendance_${startDate}_to_${endDate}${showAttendanceDetails ? "_Detail" : ""}.xlsx`);
  };

  const exportToPDF = () => {
    const pdf = new jsPDF("l", "pt");
    pdf.text(`Attendance Report (${startDate} to ${endDate})${showAttendanceDetails ? " - Detail" : ""}`, 14, 20);
    if (showAttendanceDetails) {
      const rows = detailedRows();
      pdf.autoTable({
        head: [["Staff", "Date", "Status", "Location", "Time"]],
        body: rows.map((row) => [row.Staff, row.Date, row.Status, row.Location, row.Time]),
        startY: 40,
        styles: { fontSize: 8 },
      });
    } else {
      const head = ["Staff", "Status", ...dateRange.map((date) => date.slice(8)), "P", "HD", "A"];
      const body = filteredStaff.map((staffMember) => {
        const totals = totalsForStaff(staffMember);
        return [
          getStaffName(staffMember),
          isStaffActive(staffMember) ? "Active" : "Inactive",
          ...dateRange.map((date) => statusCode(getAttendanceForStaffOnDate(staffMember, date).status)),
          totals.present, totals.halfDay, totals.absent,
        ];
      });
      pdf.autoTable({ head: [head], body, startY: 40, styles: { fontSize: 8 } });
    }
    pdf.save(`Attendance_${startDate}_to_${endDate}${showAttendanceDetails ? "_Detail" : ""}.pdf`);
  };

  const getCellClasses = (status, notEmployed) => {
    if (notEmployed) return "bg-gray-100 text-gray-500";
    if (status === "Absent") return "bg-red-100 text-red-700";
    if (status === "Half Day") return "bg-yellow-100 text-yellow-700";
    return "bg-green-100 text-green-700";
  };

  const openCorrectionModal = (staffMember, date, cell) => {
    if (!isAdmin || cell.notEmployed) return;
    setCorrectionTarget({ staffMember, date, record: cell });
    setCorrectionForm({
      status: cell.status || "Absent",
      location: cell.status === "Absent" ? "Office" : (isClientAttendance(cell) ? "Client" : "Office"),
      clientName: cell.clientName || "",
      markedAt: toDateTimeLocalValue(cell.markedAt),
      reason: "",
    });
    setCorrectionError("");
  };

  const closeCorrectionModal = () => {
    if (savingCorrection) return;
    setCorrectionTarget(null);
    setCorrectionForm(emptyCorrectionForm);
    setCorrectionError("");
  };

  const saveAttendanceCorrection = async (event) => {
    event.preventDefault();
    if (!isAdmin || !correctionTarget) return;

    const reason = correctionForm.reason.trim();
    if (!reason) {
      setCorrectionError("Correction reason is mandatory.");
      return;
    }
    if (correctionForm.status !== "Absent" && correctionForm.location === "Client" && !correctionForm.clientName.trim()) {
      setCorrectionError("Client name is required when location is Client.");
      return;
    }

    setSavingCorrection(true);
    setCorrectionError("");

    const { staffMember, date, record } = correctionTarget;
    const nowIso = new Date().toISOString();
    const previousValue = {
      status: record?.status || "Absent",
      location: record?.location || "-",
      clientName: record?.clientName || "",
      markedAt: record?.markedAt || null,
      syntheticAbsent: !record?.id,
    };
    const newStatus = correctionForm.status;
    const newLocation = newStatus === "Absent" ? "-" : (correctionForm.location === "Client" ? "Client Visit" : "Office");
    const newLocationType = newStatus === "Absent" ? "" : correctionForm.location;
    const newClientName = newStatus !== "Absent" && correctionForm.location === "Client"
      ? correctionForm.clientName.trim()
      : "";
    const newMarkedAt = newStatus === "Absent"
      ? null
      : (correctionForm.markedAt ? new Date(correctionForm.markedAt).toISOString() : nowIso);

    const correctionEntry = {
      from: previousValue,
      to: {
        status: newStatus,
        location: newLocation,
        locationType: newLocationType,
        clientName: newClientName,
        markedAt: newMarkedAt,
      },
      reason,
      correctedBy: user?.uid || currentUserId,
      correctedByName: userData?.name || userData?.username || userData?.email || "Admin",
      correctedAt: nowIso,
    };

    try {
      if (record?.id) {
        await updateAttendanceRecord(record.id, {
          status: newStatus,
          location: newLocation,
          locationType: newLocationType,
          clientName: newClientName,
          markedAt: newMarkedAt,
          originalAttendance: record.originalAttendance || previousValue,
          lastCorrectionReason: reason,
          correctedBy: correctionEntry.correctedBy,
          correctedByName: correctionEntry.correctedByName,
          correctedAt: serverTimestamp(),
          corrections: arrayUnion(correctionEntry),
        });
      } else {
        await createAttendanceRecord({
          userId: staffMember.id,
          username: getStaffName(staffMember),
          date,
          status: newStatus,
          location: newLocation,
          locationType: newLocationType,
          clientName: newClientName,
          markedAt: newMarkedAt,
          createdAt: serverTimestamp(),
          createdByAdminCorrection: true,
          originalAttendance: previousValue,
          lastCorrectionReason: reason,
          correctedBy: correctionEntry.correctedBy,
          correctedByName: correctionEntry.correctedByName,
          correctedAt: serverTimestamp(),
          corrections: [correctionEntry],
        });
      }

      await loadAttendance();
      closeCorrectionModal();
    } catch (saveError) {
      console.error("Unable to correct attendance:", saveError);
      setCorrectionError("Unable to save the correction. Please check permissions and try again.");
    } finally {
      setSavingCorrection(false);
    }
  };

  if (isStaffView) {
    const ownRows = [...dateRange].reverse().map((date) => ({
      date,
      ...getAttendanceForStaffOnDate(staff[0] || { id: currentUserId }, date),
    }));

    return (
      <div className={embedded ? "p-5" : "p-6"}>
        <h1 className="text-2xl font-bold mb-2">My Attendance</h1>
        <p className="mb-5 text-sm text-gray-600">Your attendance for the last 30 days. Other staff attendance is not accessible.</p>
        {error && <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-red-700">{error}</div>}
        <div className="mb-4 rounded-lg border bg-gray-50 px-4 py-3 text-sm text-gray-700">
          Period: <strong>{new Date(`${startDate}T00:00:00`).toLocaleDateString("en-GB")}</strong> to <strong>{new Date(`${endDate}T00:00:00`).toLocaleDateString("en-GB")}</strong>
        </div>
        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50"><tr>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Location</th>
              <th className="px-4 py-3 text-left">Client</th>
              <th className="px-4 py-3 text-left">Marked At</th>
            </tr></thead>
            <tbody className="divide-y">
              {loading ? <tr><td colSpan="5" className="p-8 text-center text-gray-500">Loading attendance…</td></tr> : ownRows.map((row) => (
                <tr key={row.date}>
                  <td className="px-4 py-3">{new Date(`${row.date}T00:00:00`).toLocaleDateString("en-GB")}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${getCellClasses(row.status, row.notEmployed)}`}>{row.status}</span></td>
                  <td className="px-4 py-3">{row.location || "—"}</td>
                  <td className="px-4 py-3">{row.clientName || "—"}</td>
                  <td className="px-4 py-3">{formatDateTime(row.markedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const invalidDateRange = Boolean(startDate && endDate && startDate > endDate);

  return (
    <div className={embedded ? "p-5" : "p-6"}>
      <h1 className="text-2xl font-bold mb-2">Attendance Register</h1>
      <p className="mb-6 text-sm text-gray-600">Monthly staff-wise attendance register. P = Full Day, HD = Half Day, A = Absent, NE = Not Employed.</p>
      {error && <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-red-700">{error}</div>}

      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div><label className="block mb-1 font-medium">Start Date</label><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border px-2 py-1 rounded" /></div>
        <div><label className="block mb-1 font-medium">End Date</label><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border px-2 py-1 rounded" /></div>
        <div><label className="block mb-1 font-medium">Staff Status</label><select value={staffStatusFilter} onChange={(e) => setStaffStatusFilter(e.target.value)} className="border px-3 py-1 rounded bg-white"><option value={STAFF_FILTERS.ACTIVE}>Active Staff</option><option value={STAFF_FILTERS.INACTIVE}>Inactive Staff</option><option value={STAFF_FILTERS.ALL}>All Staff</option></select></div>
        <label className="flex items-center gap-2 rounded border bg-white px-3 py-2 text-sm font-medium text-gray-700">
          <input type="checkbox" checked={showAttendanceDetails} onChange={(e) => setShowAttendanceDetails(e.target.checked)} />
          <span>Detail <span className="font-normal text-gray-500">(location &amp; time)</span></span>
        </label>
        <button type="button" onClick={exportToExcel} disabled={invalidDateRange || filteredStaff.length === 0} className="bg-green-600 text-white px-4 py-2 rounded disabled:opacity-50">Export Excel</button>
        <button type="button" onClick={exportToPDF} disabled={invalidDateRange || filteredStaff.length === 0} className="bg-red-600 text-white px-4 py-2 rounded disabled:opacity-50">Export PDF</button>
      </div>

      {invalidDateRange && <div className="mb-4 rounded border border-yellow-200 bg-yellow-50 px-4 py-3 text-yellow-800">End Date cannot be earlier than Start Date.</div>}
      <div className="mb-3 text-sm text-gray-600">
        Showing {filteredStaff.length} of {staff.length} staff member(s). Dates before joining or after leaving are shown as Not Employed.
        {isAdmin && <span className="ml-2 font-medium text-blue-700">Click any P, HD or A cell to correct attendance.</span>}
      </div>

      {showAttendanceDetails ? (
        <div className="overflow-x-auto bg-white shadow rounded">
          <table className="w-full text-sm border-collapse">
            <thead><tr className="bg-gray-200"><th className="p-2 border text-left">Staff</th><th className="p-2 border text-left">Date</th><th className="p-2 border text-left">Status</th><th className="p-2 border text-left">Location</th><th className="p-2 border text-left">Time Marked</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan="5" className="p-8 text-center text-gray-500">Generating attendance detail…</td></tr> : detailedRows().map((row, index) => (
                <tr key={`${row.Staff}-${row.Date}-${index}`}>
                  <td className="p-2 border font-medium">{row.Staff}</td>
                  <td className="p-2 border">{new Date(`${row.Date}T00:00:00`).toLocaleDateString("en-GB")}</td>
                  <td className="p-2 border">{row.Status}</td>
                  <td className="p-2 border">{row.Location}</td>
                  <td className="p-2 border">{row.Time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
      <div className="overflow-x-auto bg-white shadow rounded">
        <table className="w-full text-sm border-collapse">
          <thead><tr className="bg-gray-200"><th className="sticky left-0 bg-gray-200 p-2 border">Staff</th><th className="p-2 border">Status</th>{dateRange.map((date) => <th key={date} className="p-2 border min-w-[42px]">{date.slice(8)}</th>)}<th className="p-2 border">P</th><th className="p-2 border">HD</th><th className="p-2 border">A</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={dateRange.length + 5} className="p-8 text-center text-gray-500">Generating attendance register…</td></tr> : filteredStaff.map((staffMember) => {
              const totals = totalsForStaff(staffMember);
              return <tr key={staffMember.id}><td className="sticky left-0 bg-white p-2 border font-medium">{getStaffName(staffMember)}</td><td className="p-2 border">{isStaffActive(staffMember) ? "Active" : "Inactive"}</td>{dateRange.map((date) => {
                const cell = getAttendanceForStaffOnDate(staffMember, date);
                const editable = isAdmin && !cell.notEmployed;
                return (
                  <td
                    key={date}
                    className={`p-2 border text-center ${getCellClasses(cell.status, cell.notEmployed)} ${editable ? "cursor-pointer hover:ring-2 hover:ring-inset hover:ring-blue-400" : ""}`}
                    onClick={() => editable && openCorrectionModal(staffMember, date, cell)}
                    title={editable ? "Click to correct attendance" : undefined}
                    role={editable ? "button" : undefined}
                    tabIndex={editable ? 0 : undefined}
                    onKeyDown={(event) => {
                      if (editable && (event.key === "Enter" || event.key === " ")) {
                        event.preventDefault();
                        openCorrectionModal(staffMember, date, cell);
                      }
                    }}
                  >
                    {statusCode(cell.status)}
                    {cell.correctedAt && <span className="ml-1 text-[10px] font-bold" title={`Corrected: ${cell.lastCorrectionReason || "Admin correction"}`}>*</span>}
                  </td>
                );
              })}<td className="p-2 border text-center">{totals.present}</td><td className="p-2 border text-center">{totals.halfDay}</td><td className="p-2 border text-center">{totals.absent}</td></tr>;
            })}
          </tbody>
        </table>
      </div>
      )}

      {correctionTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onMouseDown={closeCorrectionModal}>
          <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between border-b px-6 py-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Correct Attendance</h2>
                <p className="mt-1 text-sm text-gray-600">
                  {getStaffName(correctionTarget.staffMember)} · {new Date(`${correctionTarget.date}T00:00:00`).toLocaleDateString("en-GB")}
                </p>
              </div>
              <button type="button" onClick={closeCorrectionModal} className="rounded p-1 text-2xl leading-none text-gray-500 hover:bg-gray-100" aria-label="Close">×</button>
            </div>

            <form onSubmit={saveAttendanceCorrection}>
              <div className="space-y-4 px-6 py-5">
                {correctionError && <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{correctionError}</div>}

                <div>
                  <label className="mb-1 block text-sm font-semibold text-gray-700">Attendance status</label>
                  <select
                    value={correctionForm.status}
                    onChange={(event) => setCorrectionForm((current) => ({ ...current, status: event.target.value }))}
                    className="w-full rounded-lg border px-3 py-2"
                  >
                    <option value="Full Day">Full Day</option>
                    <option value="Half Day">Half Day</option>
                    <option value="Absent">Absent</option>
                    <option value="Leave">Leave</option>
                  </select>
                </div>

                {correctionForm.status !== "Absent" && (
                  <>
                    <div>
                      <label className="mb-1 block text-sm font-semibold text-gray-700">Location</label>
                      <select
                        value={correctionForm.location}
                        onChange={(event) => setCorrectionForm((current) => ({ ...current, location: event.target.value, clientName: event.target.value === "Client" ? current.clientName : "" }))}
                        className="w-full rounded-lg border px-3 py-2"
                      >
                        <option value="Office">Office</option>
                        <option value="Client">Client</option>
                      </select>
                    </div>

                    {correctionForm.location === "Client" && (
                      <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700">Client name</label>
                        <input
                          value={correctionForm.clientName}
                          onChange={(event) => setCorrectionForm((current) => ({ ...current, clientName: event.target.value }))}
                          className="w-full rounded-lg border px-3 py-2"
                          placeholder="Enter client name"
                        />
                      </div>
                    )}

                    <div>
                      <label className="mb-1 block text-sm font-semibold text-gray-700">Marked time</label>
                      <input
                        type="datetime-local"
                        value={correctionForm.markedAt}
                        onChange={(event) => setCorrectionForm((current) => ({ ...current, markedAt: event.target.value }))}
                        className="w-full rounded-lg border px-3 py-2"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="mb-1 block text-sm font-semibold text-gray-700">Correction reason <span className="text-red-600">*</span></label>
                  <textarea
                    value={correctionForm.reason}
                    onChange={(event) => setCorrectionForm((current) => ({ ...current, reason: event.target.value }))}
                    className="min-h-[90px] w-full rounded-lg border px-3 py-2"
                    placeholder="Explain why this attendance is being corrected"
                    required
                  />
                </div>

                <div className="rounded-lg bg-gray-50 px-4 py-3 text-xs text-gray-600">
                  Original value, corrected value, reason, Admin identity and correction time will be preserved in the attendance record.
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
                <button type="button" onClick={closeCorrectionModal} disabled={savingCorrection} className="rounded-lg border bg-white px-4 py-2 font-medium text-gray-700 disabled:opacity-50">Cancel</button>
                <button type="submit" disabled={savingCorrection} className="rounded-lg bg-blue-700 px-5 py-2 font-semibold text-white hover:bg-blue-800 disabled:opacity-50">
                  {savingCorrection ? "Saving…" : "Save Correction"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
